import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import FolderTree, { folderRowClassName } from '@/components/doc-control/FolderTree'
import { cn } from '@/lib/utils'
import { buildFolderTree, folderLabels, moveTargets } from '../lib/folders'
import type { PhotoFolder } from '../types'

const TOP = '__top__'

interface Props {
  folder: PhotoFolder | null
  folders: PhotoFolder[]
  /** Resolves to an error text to show inline, or null when the move worked. */
  onSubmit: (parentId: string | null) => Promise<string | null>
  onClose: () => void
}

const MoveFolderDialog: React.FC<Props> = ({ folder, folders, onSubmit, onClose }) => {
  const { t } = useTranslation()
  const [target, setTarget] = useState<string>(TOP)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setTarget(folder?.parentId ?? TOP)
    setError(null)
  }, [folder])

  const tree = useMemo(
    () => (folder ? buildFolderTree(moveTargets(folders, folder.id), folderLabels(t)) : []),
    [folder, folders, t],
  )

  const submit = async () => {
    setSaving(true)
    const failure = await onSubmit(target === TOP ? null : target)
    setSaving(false)
    if (failure) setError(failure)
  }

  return (
    <Dialog open={!!folder} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Photos_move_folder')}</DialogTitle>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto">
          <div
            role="button"
            tabIndex={0}
            className={cn(folderRowClassName({ selected: target === TOP }), 'mb-0.5 pl-1')}
            onClick={() => setTarget(TOP)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setTarget(TOP)}
          >
            {t('Photos_top_level')}
          </div>
          <FolderTree folders={tree} selectedId={target === TOP ? null : target} onSelect={setTarget} showCounts={false} aria-label={t('Photos_folders')} />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Photos_move')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default MoveFolderDialog
